import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { defaultSiteContent, SiteContent, AnimationCard } from '@/mocks/siteContent';

const STORAGE_KEY = 'stingray_site_content';

interface SiteContentContextValue {
  content: SiteContent;
  updateContent: (next: SiteContent) => void;
  resetContent: () => void;
}

const SiteContentContext = createContext<SiteContentContextValue | null>(null);

function normalizeAnimationCard(card: Partial<AnimationCard> | undefined): AnimationCard {
  return {
    enabled: card?.enabled ?? true,
    icon: card?.icon || 'ri-star-line',
    title: card?.title || 'New Feature',
    desc: card?.desc || 'Description here.',
  };
}

function normalizeSiteContent(raw: Partial<SiteContent> | null | undefined): SiteContent {
  const next = raw ?? {};
  const normalizedHeroCta2 =
    next.hero?.cta2 === 'Dealer Portal'
      ? defaultSiteContent.hero.cta2
      : next.hero?.cta2;
  const normalizedFooterEmail =
    next.footer?.email === 'info@cooltechmarine.com'
      ? defaultSiteContent.footer.email
      : next.footer?.email;

  return {
    ...defaultSiteContent,
    ...next,
    hero: {
      ...defaultSiteContent.hero,
      ...next.hero,
      cta2: normalizedHeroCta2 ?? defaultSiteContent.hero.cta2,
    },
    about: {
      ...defaultSiteContent.about,
      ...next.about,
    },
    footer: {
      ...defaultSiteContent.footer,
      ...next.footer,
      email: normalizedFooterEmail ?? defaultSiteContent.footer.email,
      socials: {
        ...defaultSiteContent.footer.socials,
        ...next.footer?.socials,
      },
    },
    stats: Array.isArray(next.stats) ? next.stats : defaultSiteContent.stats,
    features: Array.isArray(next.features) ? next.features : defaultSiteContent.features,
    testimonials: Array.isArray(next.testimonials) ? next.testimonials : defaultSiteContent.testimonials,
    animation: {
      ...defaultSiteContent.animation,
      ...next.animation,
      animationEnabled: next.animation?.animationEnabled ?? true,
      cardsEnabled: next.animation?.cardsEnabled ?? true,
      mediaMode: next.animation?.mediaMode ?? 'animation',
      mediaUrl: next.animation?.mediaUrl ?? '',
      cards: Array.isArray(next.animation?.cards)
        ? next.animation.cards.map((card) => normalizeAnimationCard(card))
        : defaultSiteContent.animation.cards,
    },
  };
}

function loadFromStorage(): SiteContent {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return normalizeSiteContent(JSON.parse(raw) as Partial<SiteContent>);
  } catch {
    // ignore
  }
  return normalizeSiteContent(defaultSiteContent);
}

export function SiteContentProvider({ children }: { children: ReactNode }) {
  const [content, setContent] = useState<SiteContent>(loadFromStorage);

  const updateContent = (next: SiteContent) => {
    const normalized = normalizeSiteContent(next);
    setContent(normalized);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
    } catch {
      // ignore
    }
  };

  const resetContent = () => {
    setContent(defaultSiteContent);
    localStorage.removeItem(STORAGE_KEY);
  };

  // Sync across tabs
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && e.newValue) {
        try {
          setContent(normalizeSiteContent(JSON.parse(e.newValue) as Partial<SiteContent>));
        } catch {
          // ignore
        }
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  return (
    <SiteContentContext.Provider value={{ content, updateContent, resetContent }}>
      {children}
    </SiteContentContext.Provider>
  );
}

export function useSiteContent() {
  const ctx = useContext(SiteContentContext);
  if (!ctx) throw new Error('useSiteContent must be used within SiteContentProvider');
  return ctx;
}
